'use client';

import { useState, useRef, useCallback } from 'react';

/**
 * Hook for visual editing of SVGHMI XML.
 * Maintains a parsed XML document and applies move/delete operations.
 */
export default function useVisualEditor() {
  const [sourceDoc, setSourceDoc] = useState(null); // parsed DOMDocument
  const [lockedIds, setLockedIds] = useState(new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [editCount, setEditCount] = useState(0); // triggers re-render after edits
  const sourceDocRef = useRef(null);

  const initEditor = useCallback((xmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'application/xml');

    // Build set of locked element IDs (those with dynamic coordinates)
    const locked = new Set();
    const allElements = doc.querySelectorAll('[id]');
    allElements.forEach((el) => {
      for (const attr of el.attributes) {
        if (attr.name.match(/^hmi-bind:(x|y|cx|cy|x1|y1|x2|y2|transform)$/)) {
          locked.add(el.getAttribute('id'));
          break;
        }
      }
    });

    setLockedIds(locked);
    setSourceDoc(doc);
    sourceDocRef.current = doc;
    setSelectedId(null);
    setEditCount(0);
  }, []);

  const getModifiedXml = useCallback(() => {
    if (!sourceDocRef.current) return null;
    const serializer = new XMLSerializer();
    return serializer.serializeToString(sourceDocRef.current);
  }, []);

  const moveElement = useCallback((id, dx, dy) => {
    const doc = sourceDocRef.current;
    if (!doc || lockedIds.has(id)) return false;

    // Find element by ID (use querySelector since namespace-aware getElementById may not work)
    const el = doc.querySelector(`[id="${id}"]`);
    if (!el) return false;

    const tag = el.localName;

    if (tag === 'circle' || tag === 'ellipse') {
      const cx = parseFloat(el.getAttribute('cx') || 0) + dx;
      const cy = parseFloat(el.getAttribute('cy') || 0) + dy;
      el.setAttribute('cx', cx);
      el.setAttribute('cy', cy);
    } else if (tag === 'line') {
      el.setAttribute('x1', parseFloat(el.getAttribute('x1') || 0) + dx);
      el.setAttribute('y1', parseFloat(el.getAttribute('y1') || 0) + dy);
      el.setAttribute('x2', parseFloat(el.getAttribute('x2') || 0) + dx);
      el.setAttribute('y2', parseFloat(el.getAttribute('y2') || 0) + dy);
    } else if (['rect', 'text', 'image', 'foreignObject', 'use'].includes(tag)) {
      const x = parseFloat(el.getAttribute('x') || 0) + dx;
      const y = parseFloat(el.getAttribute('y') || 0) + dy;
      el.setAttribute('x', x);
      el.setAttribute('y', y);
    } else {
      // For <g>, <path>, <polygon>, <polyline>, etc. — use transform
      const existing = el.getAttribute('transform') || '';
      // Parse existing translate if present
      const translateMatch = existing.match(/translate\(\s*([-\d.]+)\s*[,\s]\s*([-\d.]+)\s*\)/);
      let tx = dx;
      let ty = dy;
      let newTransform;
      if (translateMatch) {
        tx += parseFloat(translateMatch[1]);
        ty += parseFloat(translateMatch[2]);
        newTransform = existing.replace(/translate\([^)]*\)/, `translate(${tx}, ${ty})`);
      } else {
        newTransform = `translate(${tx}, ${ty}) ${existing}`.trim();
      }
      el.setAttribute('transform', newTransform);
    }

    setEditCount((c) => c + 1);
    return true;
  }, [lockedIds]);

  const deleteElement = useCallback((id) => {
    const doc = sourceDocRef.current;
    if (!doc || lockedIds.has(id)) return false;

    const el = doc.querySelector(`[id="${id}"]`);
    if (!el) return false;

    el.parentNode.removeChild(el);
    setSelectedId(null);
    setEditCount((c) => c + 1);
    return true;
  }, [lockedIds]);

  return {
    sourceDoc,
    lockedIds,
    selectedId,
    setSelectedId,
    editCount,
    initEditor,
    getModifiedXml,
    moveElement,
    deleteElement,
  };
}
